import { useCallback, useEffect, useId, useRef, useState, type ChangeEvent, type ReactNode } from 'react';
import {
  RectangleStackIcon,
  FolderIcon,
  TagIcon,
  BookOpenIcon,
  UsersIcon,
  ChartBarSquareIcon,
  CloudArrowDownIcon,
  BellAlertIcon,
  FingerPrintIcon,
} from '@heroicons/react/24/outline';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import type { EngineChoice } from '@scribe/core';
import { InteractionStatus } from '@azure/msal-browser';
import { useQueryClient } from '@tanstack/react-query';
import { loginRequest } from './lib/authConfig';
import { isMock } from './lib/mock';
import { isLocalAuth } from './lib/localAuth';
import { pathOf, parsePath, type View } from './routes';
import { useApi } from './lib/ApiContext';
import { useUploadQueue } from './hooks/useUploadQueue';
import { useEngineChoices, useFolders, useMe, useTags, useVocabularies } from './lib/queries';
import { useOnDemandEnabled } from './lib/ondemandQueries';
import { IMPORT_ACCEPT_ATTR, titleFromFilename, validateImportFile } from './lib/importAudio';
import { RecordingsList } from './screens/RecordingsList';
import { RecordingDetail } from './screens/RecordingDetail';
import { RecordScreen } from './screens/RecordScreen';
import { FoldersScreen, TagsScreen } from './screens/FoldersScreen';
import { VocabularyScreen } from './screens/VocabularyScreen';
import { TranscriptionOptions } from './components/TranscriptionOptions';
import { buildJobSpec, useTranscriptionPrefs } from './hooks/useTranscriptionPrefs';
import { COMBINED_LANGUAGE_OPTIONS } from './lib/languages';
import { AdminScreen } from './screens/AdminScreen';
import { OtterImportScreen } from './screens/OtterImportScreen';
import { useOtterImportStatus } from './lib/otterImport';
import { TranscriptionSettingsScreen } from './screens/TranscriptionSettingsScreen';
import { VoicePrintScreen } from './screens/VoicePrintScreen';
import { AppShell, type FolderItem, type NavItem, type PanelTab, type TagItem } from './components/AppShell';
import { BugReportProvider } from './components/BugReportProvider';
import { RecordingProblem } from './components/RecordingProblem';
import { problemCodeForApiError, type RecordingProblemCode } from '@scribe/core/errors';
import { folderDotColor, Modal } from './components/ui';
import { Assistant } from './components/panel/Assistant';
import { ActionItems } from './components/panel/ActionItems';
import { Summary } from './components/panel/Summary';
import { useFeatureEnabled } from './lib/features';

export default function App() {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [view, setView] = useState<View>(() => parsePath(location.pathname, location.search));

  const navigate = useCallback((v: View) => {
    setView(v);
    const path = pathOf(v);
    if (path !== location.pathname + location.search) history.pushState(null, '', path);
  }, []);

  useEffect(() => {
    const onPop = () => setView(parsePath(location.pathname, location.search));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  if (isMock()) {
    return (
      <div className="shell-page">
        <div className="mock-banner">MOCK MODE — local preview, no auth, in-memory data</div>
        <Shell view={view} onNav={navigate} onSignOut={() => location.reload()} />
      </div>
    );
  }

  if (isLocalAuth()) {
    return (
      <div className="shell-page">
        <div className="mock-banner">
          LOCAL DEV AUTH — bypassing real Entra sign-in, backend must run with SCRIBE_DEV_AUTH=1
        </div>
        <Shell view={view} onNav={navigate} onSignOut={() => location.reload()} />
      </div>
    );
  }

  if (inProgress !== InteractionStatus.None) {
    return <Centered>Signing in…</Centered>;
  }

  if (!isAuthenticated) {
    return <SignedOut onSignIn={() => instance.loginRedirect(loginRequest)} />;
  }

  return (
    <div className="shell-page">
      <Shell
        view={view}
        onNav={navigate}
        onSignOut={() => {
          const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0] ?? null;
          instance.logoutRedirect({ account });
        }}
      />
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <main className="app">
      <div className="card">
        <p className="muted">{children}</p>
      </div>
    </main>
  );
}

function SignedOut({ onSignIn }: { onSignIn: () => void }) {
  return (
    <div className="landing">
      <main className="landing-stage">
        <img className="landing-logo" src="/scribe-icon-192.png" alt="" width={72} height={72} />
        <h1>Umbrage Scribe</h1>
        <p>In-house transcription — record, upload, search, and share meeting transcripts.</p>
        <button className="landing-cta" onClick={onSignIn}>
          Sign in with Microsoft
        </button>
      </main>
    </div>
  );
}

const NAV_ITEMS: { view: View; label: string; icon: ReactNode }[] = [
  {
    view: { name: 'recordings' },
    label: 'Recordings',
    icon: <RectangleStackIcon width={20} height={20} aria-hidden="true" />,
  },
  {
    view: { name: 'shared' },
    label: 'Shared with me',
    icon: <UsersIcon width={20} height={20} aria-hidden="true" />,
  },
  {
    view: { name: 'folders' },
    label: 'Folders',
    icon: <FolderIcon width={20} height={20} aria-hidden="true" />,
  },
  {
    view: { name: 'tags' },
    label: 'Tags',
    icon: <TagIcon width={20} height={20} aria-hidden="true" />,
  },
  {
    view: { name: 'vocabulary' },
    label: 'Vocabulary',
    icon: <BookOpenIcon width={20} height={20} aria-hidden="true" />,
  },
];

/** Initials for the rail's account avatar, e.g. "Nathan Rempel" -> "NR". Falls back to the
 * first two characters of an email/username when no display name is available. */
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]!;
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  const last = parts[parts.length - 1]!;
  return (first[0]! + last[0]!).toUpperCase();
}

type ShellProps = {
  view: View;
  onNav: (v: View) => void;
  onSignOut: () => void;
};

// ERRORS-1 Part B: the bug-report dialog's provider sits above the shell, so the rail's
// "Report a bug" and every RecordingProblem's "Report this problem" share one dialog. It is a
// pass-through while the `bug_reports` feature is off (FEATURES-1 runtime switch).
function Shell(props: ShellProps) {
  return (
    <BugReportProvider>
      <ShellInner {...props} />
    </BugReportProvider>
  );
}

function ShellInner({ view, onNav, onSignOut }: ShellProps) {
  const { displayName, email } = useApi();
  const [panelTab, setPanelTab] = useState<PanelTab>('summary');
  const [importError, setImportError] = useState<string | null>(null);
  // ERRORS-1: a failed import START (the createRecording call — e.g. a budget cap or an
  // expired session) renders as a catalogue problem rather than a raw server message.
  const [importProblem, setImportProblem] = useState<RecordingProblemCode | null>(null);
  // VOCAB-10: a validated file waiting on the vocabulary picker's confirm/cancel, before
  // enqueue() actually fires — null means no import dialog is open.
  const [pendingImportFile, setPendingImportFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const who = displayName || email || 'Signed in';
  const userSub = email && email !== who ? email : undefined;

  const importInputRef = useRef<HTMLInputElement>(null);
  const importQueue = useUploadQueue();
  const queryClient = useQueryClient();

  const handleImportClick = useCallback(() => {
    setImportError(null);
    importInputRef.current?.click();
  }, []);

  const handleImportFileSelected = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      // Reset the input so picking the exact same file again still fires onChange.
      e.target.value = '';
      if (!file) return;

      const validationError = validateImportFile(file);
      if (validationError) {
        setImportError(validationError);
        return;
      }

      setImportError(null);
      setPendingImportFile(file);
    },
    [],
  );

  const confirmImport = useCallback(
    async (jobSpec: Record<string, unknown>) => {
      const file = pendingImportFile;
      if (!file) return;
      setImporting(true);
      try {
        await importQueue.enqueue(file, {
          title: titleFromFilename(file.name),
          source: 'upload',
          // AI-3: a filename-derived title is a default by definition — lets the AI
          // auto-title pass replace it. No opt-out control on import in M10 (decision: the
          // import flow has no options UI today; the env default governs).
          title_is_default: true,
          // §2.7: job_spec always carries the chosen language now (the dialog builds it), so
          // the pre-VOCAB-10 "omit it when no vocabulary is picked" shape no longer applies.
          job_spec: jobSpec,
        });
        // The recording row is created server-side (in `uploaded`-pending state) as soon
        // as enqueue's createRecording call resolves — refetch the list so it shows up
        // immediately (as an in-progress row) rather than waiting for the next poll.
        await queryClient.invalidateQueries({ queryKey: ['recordings'] });
        setPendingImportFile(null);
        onNav({ name: 'recordings' });
      } catch (err) {
        setImportProblem(problemCodeForApiError(err, navigator.onLine));
        setPendingImportFile(null);
      } finally {
        setImporting(false);
      }
    },
    [pendingImportFile, importQueue, queryClient, onNav],
  );

  // ADMIN-5: the nav-rail entry is defense-in-depth, not the enforcement point — the
  // backend's `get_current_admin` is (docs/specs/admin-5.md §1). While `useMe` is loading
  // or errored, `me` is `undefined` and the entry is simply absent (no flicker states —
  // it appears once `is_admin` is known true).
  const { data: me } = useMe();
  const otterImportAvailable = !!useOtterImportStatus().data;
  // ONDEMAND-1: the auto-transcription settings entry exists only when the server flag is on
  // (absent while the settings query loads or errors — same no-flicker rule as Admin).
  const onDemandEnabled = useOnDemandEnabled();
  const voicePrintsEnabled = useFeatureEnabled('voice_prints');

  const navItems: NavItem[] = [
    ...NAV_ITEMS.map((item) => ({
      id: item.view.name,
      label: item.label,
      icon: item.icon,
      active: view.name === item.view.name,
      onClick: () => onNav(item.view),
    })),
    ...(onDemandEnabled
      ? [
          {
            id: 'transcription',
            label: 'Auto-transcription',
            icon: <BellAlertIcon width={20} height={20} aria-hidden="true" />,
            active: view.name === 'transcription',
            onClick: () => onNav({ name: 'transcription' }),
          },
        ]
      : []),
    ...(voicePrintsEnabled
      ? [
          {
            id: 'voice-print',
            label: 'Voice print',
            icon: <FingerPrintIcon width={20} height={20} aria-hidden="true" />,
            active: view.name === 'voice-print',
            onClick: () => onNav({ name: 'voice-print' }),
          },
        ]
      : []),
    ...(me?.is_admin === true
      ? [
          {
            id: 'admin',
            label: 'Admin',
            icon: <ChartBarSquareIcon width={20} height={20} aria-hidden="true" />,
            active: view.name === 'admin',
            onClick: () => onNav({ name: 'admin' }),
          },
        ]
      : []),
    // Otter history import: shown only when the backend has the feature on (its status
    // endpoint 404s otherwise, which the hook maps to `null`).
    ...(otterImportAvailable
      ? [
          {
            id: 'import',
            label: 'Import from Otter',
            icon: <CloudArrowDownIcon width={20} height={20} aria-hidden="true" />,
            active: view.name === 'import',
            onClick: () => onNav({ name: 'import' }),
          },
        ]
      : []),
  ];

  // SHARE-5: the rail's Folders/Tags sections are the primary filters for the main
  // recordings view — the dropdowns inside RecordingsList "follow" this same state (see
  // routes.ts's View.folderId/tag and RecordingsList's controlled props). Single-select,
  // toggle-to-clear (decision 4): clicking the already-active row clears that filter while
  // preserving the other one; clicking a different row swaps it in.
  const { data: folders } = useFolders();
  const { data: tags } = useTags();
  const currentFolderId = view.name === 'recordings' ? view.folderId : undefined;
  const currentTag = view.name === 'recordings' ? view.tag : undefined;

  const folderItems: FolderItem[] = (folders?.items ?? []).map((f) => {
    const active = view.name === 'recordings' && view.folderId === f.id;
    return {
      id: f.id,
      name: f.name,
      color: folderDotColor(f.id),
      active,
      onClick: () =>
        onNav({
          name: 'recordings',
          ...(active ? {} : { folderId: f.id }),
          ...(currentTag ? { tag: currentTag } : {}),
        }),
    };
  });

  const tagItems: TagItem[] = (tags?.items ?? []).map((t) => {
    const active = view.name === 'recordings' && view.tag === t.name;
    return {
      id: t.id,
      name: t.name,
      active,
      onClick: () =>
        onNav({
          name: 'recordings',
          ...(currentFolderId ? { folderId: currentFolderId } : {}),
          ...(active ? {} : { tag: t.name }),
        }),
    };
  });

  return (
    <AppShell
      navItems={navItems}
      folders={folderItems}
      tags={tagItems}
      userDisplay={who}
      {...(userSub ? { userSub } : {})}
      userInitials={initialsOf(who)}
      onSignOut={onSignOut}
      onRecord={() => onNav({ name: 'record' })}
      onImport={handleImportClick}
      onOpenRecording={(id) => onNav({ name: 'recording', id })}
      panelTab={panelTab}
      onPanelTabChange={setPanelTab}
      panelContent={
        <ContextPanelViews
          tab={panelTab}
          {...(view.name === 'recording' ? { recordingId: view.id } : {})}
          onOpenRecording={(id) => onNav({ name: 'recording', id })}
        />
      }
    >
      <input
        ref={importInputRef}
        type="file"
        accept={IMPORT_ACCEPT_ATTR}
        aria-label="Import audio file"
        onChange={handleImportFileSelected}
        style={{ display: 'none' }}
      />
      {pendingImportFile && (
        <ImportVocabularyDialog
          fileName={pendingImportFile.name}
          importing={importing}
          onCancel={() => setPendingImportFile(null)}
          onConfirm={confirmImport}
        />
      )}
      <main className="app wide">
        {importProblem && (
          <RecordingProblem code={importProblem}>
            <button className="sm" onClick={() => setImportProblem(null)}>
              Dismiss
            </button>
          </RecordingProblem>
        )}
        {importError && (
          <div className="banner banner-danger" role="alert">
            <p>{importError}</p>
            <button className="sm" onClick={() => setImportError(null)}>
              Dismiss
            </button>
          </div>
        )}
        {view.name === 'recordings' && (
          <RecordingsList
            folderId={view.folderId}
            tag={view.tag}
            onFilterChange={(f) => onNav({ name: 'recordings', ...f })}
            onOpen={(id) => onNav({ name: 'recording', id })}
          />
        )}
        {view.name === 'shared' && (
          <RecordingsList scope="shared" onOpen={(id) => onNav({ name: 'recording', id })} />
        )}
        {view.name === 'recording' && (
          <RecordingDetail id={view.id} onBack={() => onNav({ name: 'recordings' })} />
        )}
        {view.name === 'record' && (
          <RecordScreen onDone={(id) => onNav({ name: 'recording', id })} />
        )}
        {view.name === 'folders' && <FoldersScreen />}
        {view.name === 'tags' && <TagsScreen />}
        {view.name === 'vocabulary' && <VocabularyScreen />}
        {view.name === 'admin' && <AdminScreen />}
        {view.name === 'import' && (
          <OtterImportScreen onOpenRecordings={() => onNav({ name: 'recordings' })} />
        )}
        {view.name === 'transcription' && <TranscriptionSettingsScreen />}
        {view.name === 'voice-print' && <VoicePrintScreen />}
      </main>
    </AppShell>
  );
}

// Renders the context panel's active view. Summary (AI-3), Action items and Assistant
// (`useAssistantAsk`) are all backed by real data: Summary and Action items both read the
// open recording's real summary artifact themselves, given just `recordingId` (each derives
// the recording's status — and its job_spec opt-out — from its own `useRecording` call).
// Deliberately dumb: forwards `recordingId` (+ a navigate callback for Assistant citations —
// the same destination a global-search result opens) and nothing else.
function ContextPanelViews({
  tab,
  recordingId,
  onOpenRecording,
}: {
  tab: PanelTab;
  recordingId?: string;
  onOpenRecording: (id: string) => void;
}) {
  // `assistant` switch guard: the tab is hidden when the feature is off, but never trust
  // stale tab state to render a feature an admin has switched off.
  const assistantOn = useFeatureEnabled('assistant');
  if (tab === 'assistant' && assistantOn) {
    return (
      <Assistant
        {...(recordingId ? { recordingId } : {})}
        onOpenCitation={(c) => onOpenRecording(c.recording_id)}
      />
    );
  }
  if (tab === 'action-items') return <ActionItems {...(recordingId ? { recordingId } : {})} />;
  return <Summary {...(recordingId ? { recordingId } : {})} />;
}

// VOCAB-10: a small confirm step between picking a file and actually importing it, so the
// import-audio flow (like RecordScreen) gets a chance to bias transcription toward a
// published vocabulary before any audio uploads. "None" is the default — confirming without
// picking one reproduces the pre-VOCAB-10 request shape exactly (no job_spec sent).
function ImportVocabularyDialog({
  fileName,
  importing,
  onCancel,
  onConfirm,
}: {
  fileName: string;
  importing: boolean;
  onCancel: () => void;
  onConfirm: (jobSpec: Record<string, unknown>) => void;
}) {
  const { data: vocabularies } = useVocabularies();
  const { language, setLanguage, vocabularyName, setVocabularyName } = useTranscriptionPrefs(
    vocabularies?.items,
  );
  const titleId = useId();
  // WHISPER-1: per-recording engine; only offered when the server lists more than AWS.
  const engines = useEngineChoices();
  const [engine, setEngine] = useState<EngineChoice>('aws');

  return (
    <Modal onClose={importing ? () => {} : onCancel} labelledBy={titleId}>
      <h2 id={titleId}>Import audio</h2>
      <p className="muted">{fileName}</p>
      <TranscriptionOptions
        idPrefix="import"
        language={language}
        onLanguageChange={setLanguage}
        vocabularyName={vocabularyName}
        onVocabularyChange={setVocabularyName}
        vocabularies={vocabularies?.items}
        disabled={importing}
        // An import is always a batch transcription — there is no live session — so Combined
        // is always available here.
        engine={engine}
        onEngineChange={setEngine}
        engines={engines}
      />
      <div className="row">
        <button
          disabled={importing}
          onClick={() =>
            onConfirm(buildJobSpec(language, vocabularyName, COMBINED_LANGUAGE_OPTIONS, engine))
          }
        >
          {importing ? 'Importing…' : 'Import recording'}
        </button>
        <button type="button" className="ghost" disabled={importing} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </Modal>
  );
}
